import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Scan, Bold, Underline, Strikethrough, Italic, X } from "lucide-react";

const fontFamilies = [
  "Arial, sans-serif",
  "'Courier New', monospace",
  "'Times New Roman', serif",
  "'Comic Sans MS', cursive",
  "'Roboto', sans-serif",
];

const TrueFalseToolbar = ({
  questionId,
  isLeftSidebarOpen,
  leftSidebarWidth = 0,
  isRightSidebarOpen,
  windowWidth,
  selectedComponent,
  onComponentUpdate,
  onDeselect,
}) => {
  const [isMobile, setIsMobile] = useState(windowWidth < 768);
  const [fontSizeInput, setFontSizeInput] = useState(
    String(selectedComponent?.fontSize ?? 14)
  );
  const [borderRadiusInput, setBorderRadiusInput] = useState(
    String(selectedComponent?.borderRadius ?? 12)
  );

  useEffect(() => {
    setIsMobile(windowWidth < 768);
  }, [windowWidth]);

  useEffect(() => {
    setFontSizeInput(String(selectedComponent?.fontSize ?? 14));
  }, [selectedComponent?.fontSize]);

  useEffect(() => {
    setBorderRadiusInput(String(selectedComponent?.borderRadius ?? 12));
  }, [selectedComponent?.borderRadius]);

  const calculatePosition = () => {
    if (windowWidth < 768) {
      return {
        left: "50%",
        transform: "translateX(-50%)",
      };
    }
    const leftSidebarOffset = isLeftSidebarOpen ? leftSidebarWidth / 2 : 0;
    const rightSidebarOffset = isRightSidebarOpen ? -32 : 0;
    const totalOffset = leftSidebarOffset + rightSidebarOffset;
    const leftOffset =
      totalOffset !== 0 ? `calc(50% + ${totalOffset}px)` : "50%";

    return {
      left: leftOffset,
      transform: "translateX(-50%)",
    };
  };

  const position = calculatePosition();

  // Helper to toggle font style boolean values
  const toggleStyle = (key) => {
    onComponentUpdate(questionId, selectedComponent.id, {
      fontStyles: {
        ...selectedComponent.fontStyles,
        [key]: !selectedComponent.fontStyles?.[key],
      },
    });
  };

  // Helpers for numeric inputs
  const clampNumber = (value, min, max, fallback) => {
    const parsed = parseInt(String(value), 10);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(max, Math.max(min, parsed));
  };

  const commitFontSize = () => {
    if (!selectedComponent?.id) return;
    const clamped = clampNumber(fontSizeInput, 8, 48, 14);
    setFontSizeInput(String(clamped));
    if (selectedComponent?.fontSize !== clamped) {
      onComponentUpdate(questionId, selectedComponent.id, {
        fontSize: clamped,
      });
    }
  };

  const resetFontSizeInput = () => {
    setFontSizeInput(String(selectedComponent?.fontSize ?? 14));
  };

  const commitBorderRadius = () => {
    if (!selectedComponent?.id) return;
    const clamped = clampNumber(borderRadiusInput, 0, 50, 12);
    setBorderRadiusInput(String(clamped));
    if (selectedComponent?.borderRadius !== clamped) {
      onComponentUpdate(questionId, selectedComponent.id, {
        borderRadius: clamped,
      });
    }
  };

  const resetBorderRadiusInput = () => {
    setBorderRadiusInput(String(selectedComponent?.borderRadius ?? 12));
  };

  return (
    <div
      className="fixed z-50 transition-all duration-300"
      style={{
        left: position.left,
        transform: position.transform,
        bottom: isMobile ? "calc(34px + env(safe-area-inset-bottom, 0px))" : 34,
      }}
    >
      <motion.div
        key={questionId}
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className={`bg-white rounded-xl shadow-xl border border-gray-200 relative`}
        style={{
          width: isMobile ? "calc(100vw - 24px)" : "600px",
          maxWidth: "calc(100vw - 24px)",
          maxHeight: isMobile ? "40vh" : "none",
          overflowY: isMobile ? "auto" : "visible",
        }}
      >
        <button
          onClick={onDeselect}
          className={`${
            isMobile ? "top-2 right-2" : "-top-3 right-2"
          } absolute bg-white rounded-full ${
            isMobile ? "p-1" : "p-1.5"
          } shadow-md border border-gray-200`}
          aria-label="Close"
          title="Close"
        >
          <X size={isMobile ? 14 : 16} className="text-gray-700" />
        </button>

        <div className={`${isMobile ? "px-2 py-3" : "px-4 py-4"} w-full`}>
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex flex-col gap-2 flex-1">
              <p className="text-xs text-black font-semibold mb-1">
                Text Options
              </p>
              {/* Font Family & Size */}
              <div className="flex gap-1">
                <label className="flex-1">
                  {/* <span className="text-xs font-medium text-gray-700">
                    Font Family
                  </span> */}
                  <select
                    value={selectedComponent?.fontFamily || fontFamilies[0]}
                    onChange={(e) =>
                      onComponentUpdate(questionId, selectedComponent.id, {
                        fontFamily: e.target.value,
                      })
                    }
                    className="block w-full rounded-md border border-gray-300 p-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    {fontFamilies.map((font) => (
                      <option key={font} value={font}>
                        {font.split(",")[0]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="w-12 md:w-10">
                  {/* <span className="text-xs font-medium text-gray-700">
                    Font Size
                  </span> */}
                  <input
                    type="number"
                    min={8}
                    max={48}
                    value={fontSizeInput}
                    onChange={(e) => {
                      const next = e.target.value;
                      setFontSizeInput(next);
                      const parsed = parseInt(next, 10);
                      if (
                        Number.isFinite(parsed) &&
                        parsed >= 8 &&
                        parsed <= 48 &&
                        selectedComponent?.fontSize !== parsed
                      ) {
                        onComponentUpdate(questionId, selectedComponent.id, {
                          fontSize: parsed,
                        });
                      }
                    }}
                    onBlur={commitFontSize}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitFontSize();
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        resetFontSizeInput();
                      }
                    }}
                    className="block w-full rounded-md border border-gray-300 p-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </label>
              </div>
              {/* Font Styles */}
              <div className="flex gap-1 flex-wrap">
                {["bold", "italic", "underline", "lineThrough"].map((style) => {
                  const iconMap = {
                    bold: <Bold className="h-5 w-5" />,
                    italic: <Italic className="h-5 w-5" />,
                    underline: <Underline className="h-5 w-5" />,
                    lineThrough: <Strikethrough className="h-5 w-5" />,
                  };
                  const titleMap = {
                    bold: "Bold",
                    italic: "Italic",
                    underline: "Underline",
                    lineThrough: "Strikethrough",
                  };
                  const isActive = Boolean(
                    selectedComponent?.fontStyles?.[style]
                  );
                  return (
                    <button
                      key={style}
                      onClick={() => toggleStyle(style)}
                      className={`w-8 h-8 rounded border text-sm font-semibold flex items-center justify-center select-none
                      ${
                        isActive
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                      }`}
                      title={titleMap[style]}
                      aria-pressed={isActive}
                      type="button"
                    >
                      {iconMap[style]}
                    </button>
                  );
                })}
              </div>
              {/* Font Color */}
              <label>
                <p className="text-xs text-black font-semibold mb-1">
                  Font Color
                </p>
                <div className="flex justify-center items-center w-full h-8 overflow-hidden rounded-md shadow">
                  <input
                    type="color"
                    value={selectedComponent?.fontColor || "#000000"}
                    onChange={(e) =>
                      onComponentUpdate(questionId, selectedComponent.id, {
                        fontColor: e.target.value,
                      })
                    }
                    className="w-[calc(100%+8px)] h-14 -m-1 border-none p-0 focus:outline-none ring-0 outline-none"
                  />
                </div>
              </label>
            </div>
            <div className="flex flex-col gap-3 flex-1">
              <p className="text-xs text-black font-semibold mb-1">
                Option Colors
              </p>
              {/* Colors and Border Radius */}
              <div className="grid grid-cols-2 gap-2">
                <label className="flex items-center gap-2">
                  <div className="w-8 h-8 min-w-8 min-h-8 max-w-8 max-h-8 flex justify-center items-center overflow-hidden border border-white rounded-lg shadow">
                    <input
                      type="color"
                      value={selectedComponent?.leftBoxColor || "#22c55e"}
                      onChange={(e) =>
                        onComponentUpdate(questionId, selectedComponent.id, {
                          leftBoxColor: e.target.value,
                        })
                      }
                      className="w-10 h-10 min-w-10 min-h-10 max-w-10 max-h-10 -m-1 border-none p-0 focus:outline-none ring-0 outline-none"
                    />
                  </div>
                  <span className="text-xs font-medium text-black leading-tight">
                    Left option
                  </span>
                </label>
                <label className="flex items-center gap-2">
                  <div className="w-8 h-8 min-w-8 min-h-8 max-w-8 max-h-8 flex justify-center items-center overflow-hidden border border-white rounded-lg shadow">
                    <input
                      type="color"
                      value={selectedComponent?.rightBoxColor || "#ef4444"}
                      onChange={(e) =>
                        onComponentUpdate(questionId, selectedComponent.id, {
                          rightBoxColor: e.target.value,
                        })
                      }
                      className="w-10 h-10 min-w-10 min-h-10 max-w-10 max-h-10 -m-1 border-none p-0 focus:outline-none ring-0 outline-none"
                    />
                  </div>
                  <span className="text-xs font-medium text-black leading-tight">
                    Right option
                  </span>
                </label>
              </div>
              <label>
                <p className="text-xs text-black font-semibold mb-1">
                  Background Color
                </p>
                <div className="flex justify-center items-center w-full h-8 overflow-hidden rounded-md shadow ">
                  <input
                    type="color"
                    value={selectedComponent?.backgroundColor || "#ffffff"}
                    onChange={(e) =>
                      onComponentUpdate(questionId, selectedComponent.id, {
                        backgroundColor: e.target.value,
                      })
                    }
                    className="w-[calc(100%+8px)] h-14 -m-1 border-none p-0 focus:outline-none ring-0 outline-none"
                  />
                </div>
              </label>
            </div>
            <div className="flex flex-col gap-3 flex-1">
              <label className="flex-1">
                <p className="text-xs text-black font-semibold mb-1">
                  Corner Radius
                </p>
                <div className="flex items-center gap-2 px-2 w-full h-8 overflow-hidden rounded-md bg-gray-300">
                  <Scan className="h-5 w-5" />
                  <input
                    type="number"
                    min={0}
                    max={50}
                    value={borderRadiusInput}
                    onChange={(e) => {
                      const next = e.target.value;
                      setBorderRadiusInput(next);
                      const parsed = parseInt(next, 10);
                      if (
                        Number.isFinite(parsed) &&
                        parsed >= 0 &&
                        parsed <= 50 &&
                        selectedComponent?.borderRadius !== parsed
                      ) {
                        onComponentUpdate(questionId, selectedComponent.id, {
                          borderRadius: parsed,
                        });
                      }
                    }}
                    onBlur={commitBorderRadius}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitBorderRadius();
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        resetBorderRadiusInput();
                      }
                    }}
                    className="w-10 border-none text-[11px] font-medium bg-transparent focus:outline-none"
                  />
                </div>
              </label>
              <label className="flex-1">
                <p className="text-xs text-black font-semibold mb-1">
                  Border Color
                </p>
                <div className="flex justify-center items-center w-full h-8 overflow-hidden rounded-md shadow ">
                  <input
                    type="color"
                    value={selectedComponent?.borderColor || "#d1d5db"}
                    onChange={(e) =>
                      onComponentUpdate(questionId, selectedComponent.id, {
                        borderColor: e.target.value,
                      })
                    }
                    className="w-[calc(100%+8px)] h-14 -m-1 border-none p-0 focus:outline-none ring-0 outline-none"
                  />
                </div>
              </label>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default TrueFalseToolbar;
